// Live lathe panel: pick an input, monitor it through the virtual needle in real time, and cut a dubplate.
import type { AudioSource } from "../audio/source";
import type { Mode, Values } from "../params";
import { resampleSignal } from "../dsp/resample";
import { drawSpectrum } from "../ui/spectrum";
import { liveConfigs } from "./config";
import type { Monitor } from "./engineCore";
import { LatheView, type LatheGeometry } from "./latheView";
import type { FromWorklet, ToWorklet } from "./worklet";
import workletUrl from "./worklet.ts?worker&url";

export interface LiveHost {
  mode(): Mode;
  values(): Values;
  source(): AudioSource;
  onDubplate(src: AudioSource): void;
}

const SR = 44100;

function pitchOf(mode: Mode, v: Values): number {
  if (mode === "spiral") return (2 * (v.amplitude as number) + (v.spacing as number)) / (v.dpi as number);
  if (mode === "print3d") {
    const amp = ((v.amplitude as number) * (v.micronsPerLayer as number)) / 25400;
    return (v.grooveWidth as number) / (v.dpi as number) + 2 * (v.bevel as number) * amp;
  }
  return 0;
}

/** Seconds of audio that fit on the record/strip with the current settings. */
export function capacitySeconds(mode: Mode, v: Values): number {
  if (mode === "linear") {
    const rows = Math.max(1, Math.floor(((v.cutterHeight as number) - 2 * (v.margin as number)) / (v.rowPitch as number)));
    return (rows * ((v.cutterWidth as number) - 2 * (v.margin as number))) / (v.speed as number);
  }
  const extra = mode === "spiral" ? (v.leadIn as number) + (v.leadOut as number) : 2;
  const revs = ((v.outerRad as number) - (v.innerRad as number)) / pitchOf(mode, v) - extra;
  return Math.max(0, (revs * 60) / (v.rpm as number));
}

export class LivePanel {
  private ctx: AudioContext | null = null;
  private rate = SR;
  private node: AudioWorkletNode | null = null;
  private input: AudioNode | null = null;
  private stream: MediaStream | null = null;
  private anaIn: AnalyserNode | null = null;
  private anaOut: AnalyserNode | null = null;
  private view: LatheView;
  private monitor: Monitor = "improved";
  private gainDb = 0;
  private monoSum = false;
  private follow = true;
  private fixedRadius = 4;
  private followStart = performance.now();
  private timer = 0;
  private chunks: { L: Float32Array; R: Float32Array }[] = [];
  private cutting = false;
  private peakHold = 0;
  private lastClips = { original: 0, improved: 0 };
  private clipFlash = { original: 0, improved: 0 };
  private els: Record<string, HTMLElement> = {};

  constructor(private root: HTMLElement, canvas: HTMLCanvasElement, private host: LiveHost) {
    this.view = new LatheView(canvas);
    this.build();
  }

  get running() {
    return this.node !== null;
  }

  private build() {
    this.root.innerHTML = `
      <h3>Lathe <span class="muted small">— monitor live input through the virtual needle, then cut a dubplate</span></h3>
      <div class="live-row">
        <select id="liveInput" style="flex:1"></select>
        <button id="liveStart" class="primary">▶ Start</button>
      </div>
      <div class="muted small" id="liveErr"></div>
      <h3>Monitor <span class="muted small">(keys 1 · 2 · 3)</span></h3>
      <div class="seg" id="liveMon">
        <button data-m="source">1 Source</button><button data-m="original">2 Original</button><button data-m="improved" class="on">3 Improved</button>
      </div>
      <div class="live-row" style="margin-top:10px">
        <label class="small" style="width:70px">Input gain</label>
        <input id="liveGain" type="range" min="-24" max="24" step="1" value="0" style="flex:1" />
        <span id="liveGainVal" class="small muted" style="width:46px;text-align:right">0 dB</span>
      </div>
      <div class="meter"><div id="liveMeter"></div></div>
      <div class="live-row small">
        <span class="lamp" id="clipO">overcut · original</span>
        <span class="lamp" id="clipI">overcut · improved</span>
        <span class="muted" id="liveLag"></span>
      </div>
      <div class="live-row small" style="margin-top:8px">
        <label><input type="checkbox" id="liveFollow" checked /> Needle follows the spiral</label>
      </div>
      <div class="live-row small">
        <span class="muted" style="width:70px">radius</span>
        <input id="liveRadius" type="range" min="1" max="6" step="0.01" value="4" style="flex:1" disabled />
        <span id="liveRadiusVal" class="muted" style="width:52px;text-align:right"></span>
      </div>
      <label class="small"><input type="checkbox" id="liveMono" /> Sum to mono (hear 3D hill-and-dale cancel)</label>
      <h3>Dubplate</h3>
      <div class="live-row">
        <button id="liveCut" class="cut" disabled>● Cut</button>
        <div class="small" style="flex:1">
          <div id="liveCutTime">—</div>
          <div class="cutbar"><div id="liveCutBar"></div></div>
        </div>
      </div>
      <div class="muted small" id="liveCutHint">Cut starts at the outer groove and the head moves inward at the real groove pitch. Lift (or a full record) loads the dubplate as the source: then play it back, compare and export it from the Compare tab.</div>
      <h3>What the cutter is doing</h3>
      <div class="small" id="liveInfo"></div>
      <h3>Live spectrum</h3>
      <canvas id="liveSpec" height="160"></canvas>
      <p class="muted small">🎧 Use headphones with a microphone, or the speakers will feed back into the input.
      "System / tab audio" lets you cut whatever is playing (in the share dialog, choose a tab or screen and tick “share audio”).</p>`;
    const q = <T extends HTMLElement>(id: string) => this.root.querySelector<T>(`#${id}`)!;
    for (const id of ["liveInput", "liveStart", "liveErr", "liveGain", "liveGainVal", "liveMeter", "clipO", "clipI", "liveLag", "liveFollow", "liveRadius", "liveRadiusVal", "liveMono", "liveCut", "liveCutTime", "liveCutBar", "liveInfo", "liveSpec"])
      this.els[id] = q(id);
    void this.fillInputs();
    navigator.mediaDevices?.addEventListener?.("devicechange", () => void this.fillInputs());
    this.els.liveStart.onclick = () => (this.running ? this.stop() : void this.start());
    this.root.querySelectorAll<HTMLButtonElement>("#liveMon button").forEach((b) => (b.onclick = () => this.setMonitor(b.dataset.m as Monitor)));
    (this.els.liveGain as HTMLInputElement).oninput = (e) => {
      this.gainDb = Number((e.target as HTMLInputElement).value);
      this.els.liveGainVal.textContent = `${this.gainDb > 0 ? "+" : ""}${this.gainDb} dB`;
      this.sendConfig();
    };
    (this.els.liveFollow as HTMLInputElement).onchange = (e) => {
      this.follow = (e.target as HTMLInputElement).checked;
      (this.els.liveRadius as HTMLInputElement).disabled = this.follow;
      this.followStart = performance.now();
    };
    (this.els.liveRadius as HTMLInputElement).oninput = (e) => { this.fixedRadius = Number((e.target as HTMLInputElement).value); };
    (this.els.liveMono as HTMLInputElement).onchange = (e) => { this.monoSum = (e.target as HTMLInputElement).checked; this.sendConfig(); };
    this.els.liveCut.onclick = () => (this.cutting ? this.lift() : this.cut());
    window.addEventListener("keydown", (e) => {
      if (!this.running || (e.target as HTMLElement).matches("input, select, textarea")) return;
      const m = ({ "1": "source", "2": "original", "3": "improved" } as Record<string, Monitor>)[e.key];
      if (m) this.setMonitor(m);
    });
  }

  private async fillInputs() {
    const sel = this.els.liveInput as HTMLSelectElement;
    const prev = sel.value;
    sel.innerHTML = "";
    sel.add(new Option("Default microphone / line-in", "default"));
    try {
      const devs = await navigator.mediaDevices.enumerateDevices();
      devs.filter((d) => d.kind === "audioinput" && d.deviceId && d.deviceId !== "default")
        .forEach((d, i) => sel.add(new Option(d.label || `Input ${i + 1}`, `dev:${d.deviceId}`)));
    } catch { /* no permission yet */ }
    if (typeof navigator.mediaDevices?.getDisplayMedia === "function") sel.add(new Option("System / tab audio (share)…", "display"));
    sel.add(new Option("Current source, looped (no mic needed)", "source"));
    if ([...sel.options].some((o) => o.value === prev)) sel.value = prev;
  }

  private setMonitor(m: Monitor) {
    this.monitor = m;
    this.root.querySelectorAll<HTMLButtonElement>("#liveMon button").forEach((b) => b.classList.toggle("on", b.dataset.m === m));
    this.view.algo = m === "original" ? "original" : "improved";
    this.sendConfig();
  }

  private radius(): number {
    const v = this.host.values();
    const outer = v.outerRad as number, inner = v.innerRad as number;
    if (this.view.cutting) return this.view.radius();
    if (!this.follow) return Math.min(outer, Math.max(inner, this.fixedRadius));
    const pitch = pitchOf(this.host.mode(), v) || 0.025;
    const span = Math.max(0.01, outer - inner);
    const travelled = ((performance.now() - this.followStart) / 1000) * ((v.rpm as number) / 60) * pitch;
    return outer - (travelled % span);
  }

  /** Re-read mode/params from the main UI (call on every parameter change). */
  refresh() {
    const mode = this.host.mode(), v = this.host.values();
    const r = this.els.liveRadius as HTMLInputElement;
    r.min = String(v.innerRad ?? 1);
    r.max = String(v.outerRad ?? 6);
    const amp = mode === "spiral" ? (v.amplitude as number) / (v.dpi as number) : mode === "print3d" ? ((v.amplitude as number) * (v.micronsPerLayer as number)) / 25400 : 0;
    const steps = v.simQuantize ? (v.amplitude as number) : 0;
    const geo: LatheGeometry = {
      polar: mode !== "linear",
      rpm: (v.rpm as number) ?? 45,
      diameter: (v.diameter as number) ?? 12,
      innerHole: (v.innerHole as number) ?? 0.286,
      outerRad: (v.outerRad as number) ?? 5.75,
      innerRad: (v.innerRad as number) ?? 2.25,
      pitch: pitchOf(mode, v),
      amp,
      ampSteps: steps,
      gridStep: steps ? 1 / steps : 0,
    };
    this.view.geo = geo;
    (this.els.liveCut as HTMLButtonElement).disabled = !this.running;
    this.sendConfig();
  }

  private sendConfig() {
    if (!this.node) return;
    const mode = this.host.mode(), v = this.host.values();
    const radius = this.radius();
    const { original, improved, info } = liveConfigs(mode, v, radius, this.sr);
    const msg: ToWorklet = { type: "config", original, improved, gain: Math.pow(10, this.gainDb / 20), monitor: this.monitor, monoSum: this.monoSum };
    this.node.port.postMessage(msg);
    this.els.liveInfo.innerHTML = `<div><b style="color:var(--orig)">Original:</b> ${info.original}</div><div><b style="color:var(--impr)">Improved:</b> ${info.improved}</div>`;
    this.els.liveRadiusVal.textContent = info.radius !== null ? `${radius.toFixed(2)}″` : "—";
    this.view.monitorRadius = radius;
    this.view.info = `${info.original.split(",")[0]} (original)\n${info.improved.split(",")[0]} (improved)`;
  }

  async start() {
    this.els.liveErr.textContent = "";
    try {
      const sel = (this.els.liveInput as HTMLSelectElement).value;
      this.ctx = new AudioContext({ sampleRate: SR, latencyHint: "interactive" });
      const raw = { echoCancellation: false, noiseSuppression: false, autoGainControl: false, channelCount: 2 };
      if (sel === "source") {
        const src = this.host.source();
        const buf = this.ctx.createBuffer(2, src.channels[0].length, src.sampleRate);
        buf.copyToChannel(src.channels[0] as Float32Array<ArrayBuffer>, 0);
        buf.copyToChannel((src.channels[1] ?? src.channels[0]) as Float32Array<ArrayBuffer>, 1);
        const n = this.ctx.createBufferSource();
        n.buffer = buf;
        n.loop = true;
        n.start();
        this.input = n;
      } else {
        if (sel === "display") {
          this.stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: raw });
          this.stream.getVideoTracks().forEach((t) => t.stop());
          if (!this.stream.getAudioTracks().length) throw new Error("No audio was shared. Pick a tab/screen and tick “share audio”.");
        } else {
          const deviceId = sel.startsWith("dev:") ? { exact: sel.slice(4) } : undefined;
          this.stream = await navigator.mediaDevices.getUserMedia({ audio: { ...raw, deviceId } });
          void this.fillInputs(); // labels become available after permission
        }
        try {
          this.input = this.ctx.createMediaStreamSource(this.stream);
        } catch {
          // Firefox can't connect a mic to a context at a different rate: run at the device rate instead
          void this.ctx.close();
          this.ctx = new AudioContext({ latencyHint: "interactive" });
          this.input = this.ctx.createMediaStreamSource(this.stream);
        }
      }
      this.rate = this.ctx.sampleRate;
      await this.ctx.audioWorklet.addModule(workletUrl);
      this.node = new AudioWorkletNode(this.ctx, "lathe", { numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [2], channelCount: 2, channelCountMode: "explicit" });
      this.node.port.onmessage = (e: MessageEvent<FromWorklet>) => this.onMessage(e.data);
      this.anaIn = this.ctx.createAnalyser();
      this.anaOut = this.ctx.createAnalyser();
      this.anaIn.fftSize = this.anaOut.fftSize = 4096;
      this.anaIn.smoothingTimeConstant = this.anaOut.smoothingTimeConstant = 0.75;
      this.input.connect(this.node);
      this.input.connect(this.anaIn);
      this.node.connect(this.anaOut);
      this.node.connect(this.ctx.destination);
      this.view.sampleRate = this.ctx.sampleRate;
      this.refresh();
      this.timer = window.setInterval(() => { this.sendConfig(); this.drawSpectrum(); this.updateCut(); }, 120);
      this.view.start();
      this.els.liveStart.textContent = "■ Stop";
      this.els.liveStart.classList.add("on");
      (this.els.liveCut as HTMLButtonElement).disabled = false;
    } catch (err) {
      this.els.liveErr.textContent = `Could not start: ${err instanceof Error ? err.message : err}`;
      this.stop();
    }
  }

  stop() {
    if (this.cutting) {
      // let the worklet flush the last recorded chunk and confirm before tearing down
      this.lift();
      setTimeout(() => this.teardown(), 400);
      return;
    }
    this.teardown();
  }

  private teardown() {
    clearInterval(this.timer);
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
    try { (this.input as AudioBufferSourceNode | null)?.stop?.(); } catch { /* not a buffer source */ }
    this.node?.disconnect();
    this.node = null;
    void this.ctx?.close();
    this.ctx = null;
    this.view.stop();
    this.view.setIdle();
    this.els.liveStart.textContent = "▶ Start";
    this.els.liveStart.classList.remove("on");
    (this.els.liveCut as HTMLButtonElement).disabled = true;
  }

  /** Show/hide: the lathe view only animates while visible and running. */
  setVisible(on: boolean) {
    if (on && this.running) this.view.start();
    else if (!on) this.view.stop();
    if (on) this.refresh();
    if (on && !this.running) this.view.renderOnce();
  }

  private cut() {
    if (!this.node) return;
    this.chunks = [];
    this.cutting = true;
    this.view.beginCut(this.view.now());
    this.node.port.postMessage({ type: "cut", on: true } satisfies ToWorklet);
    this.els.liveCut.textContent = "■ Lift";
    this.els.liveCut.classList.add("on");
  }

  private lift() {
    if (!this.cutting) return;
    this.cutting = false;
    this.node?.port.postMessage({ type: "cut", on: false } satisfies ToWorklet);
    this.view.endCut();
    this.els.liveCut.textContent = "● Cut";
    this.els.liveCut.classList.remove("on");
    if (!this.node) this.finishDubplate();
  }

  private finishDubplate() {
    const n = this.chunks.reduce((s, c) => s + c.L.length, 0);
    const sr = this.sr;
    if (n < sr * 0.5) {
      this.els.liveCutTime.textContent = "cut too short — nothing loaded";
      return;
    }
    let L: Float32Array = new Float32Array(n), R: Float32Array = new Float32Array(n);
    let o = 0;
    for (const c of this.chunks) { L.set(c.L, o); R.set(c.R, o); o += c.L.length; }
    this.chunks = [];
    if (sr !== SR) { L = resampleSignal(L, sr, SR); R = resampleSignal(R, sr, SR); }
    const d = new Date();
    const name = `dubplate-${String(d.getHours()).padStart(2, "0")}${String(d.getMinutes()).padStart(2, "0")}${String(d.getSeconds()).padStart(2, "0")}`;
    this.els.liveCutTime.textContent = `${name}: ${(n / sr).toFixed(1)} s loaded as the source`;
    this.host.onDubplate({ name, sampleRate: SR, channels: [L, R] });
  }

  private updateCut() {
    const cap = capacitySeconds(this.host.mode(), this.host.values());
    const s = this.view.cutSeconds();
    if (this.cutting && s !== null) {
      this.els.liveCutTime.textContent = `● ${s.toFixed(1)} s of ${cap.toFixed(0)} s · ${Math.max(0, cap - s).toFixed(0)} s left`;
      (this.els.liveCutBar as HTMLElement).style.width = `${Math.min(100, (s / cap) * 100)}%`;
      if (s >= cap) this.lift(); // reached the inner groove: the lathe lifts
    } else if (!this.cutting && !this.els.liveCutTime.textContent?.startsWith("dubplate")) {
      this.els.liveCutTime.textContent = `blank holds ${cap.toFixed(0)} s at ${this.host.values().rpm ?? ""} rpm`;
      (this.els.liveCutBar as HTMLElement).style.width = "0%";
    }
  }

  private onMessage(m: FromWorklet) {
    if (m.type === "chunk") { this.chunks.push({ L: m.L, R: m.R }); return; }
    if (m.type === "cutDone") { this.finishDubplate(); return; }
    this.view.onStatus(m);
    // meter (dBFS, -48..0)
    this.peakHold = Math.max(m.inPeak, this.peakHold * 0.85);
    const db = 20 * Math.log10(this.peakHold + 1e-9);
    const meter = this.els.liveMeter as HTMLElement;
    meter.style.width = `${Math.max(0, Math.min(100, ((db + 48) / 48) * 100))}%`;
    meter.style.background = db > -1 ? "var(--bad)" : db > -9 ? "var(--warn)" : "var(--good)";
    const now = performance.now();
    for (const a of ["original", "improved"] as const) {
      if (m.clips[a] > this.lastClips[a]) this.clipFlash[a] = now;
      this.lastClips[a] = m.clips[a];
    }
    this.els.clipO.classList.toggle("hot", now - this.clipFlash.original < 400);
    this.els.clipI.classList.toggle("hot", now - this.clipFlash.improved < 400);
    const dropPct = m.kept ? (100 * m.dropped) / (m.dropped + m.kept) : 0;
    this.els.liveLag.textContent = `${m.lagMs > 1 ? `original lags ${m.lagMs.toFixed(0)} ms · ` : ""}${dropPct > 0.5 ? `${dropPct.toFixed(0)}% points dropped` : ""}`;
  }

  private drawSpectrum() {
    if (!this.anaIn || !this.anaOut) return;
    const a = new Float32Array(this.anaIn.frequencyBinCount), b = new Float32Array(this.anaOut.frequencyBinCount);
    this.anaIn.getFloatFrequencyData(a);
    this.anaOut.getFloatFrequencyData(b);
    let max = -200;
    for (let i = 0; i < a.length; i++) max = Math.max(max, a[i], b[i]);
    const off = -max;
    for (let i = 0; i < a.length; i++) { a[i] += off; b[i] += off; }
    const color = this.monitor === "original" ? "#ff5a4f" : this.monitor === "improved" ? "#3fa7ff" : "#b8bec8";
    drawSpectrum(this.els.liveSpec as HTMLCanvasElement, [
      { db: a, color: "#6c727c", label: "input" },
      { db: b, color, label: `monitor · ${this.monitor}` },
    ], this.sr);
  }

  /** The context rate: 44.1 kHz normally, the device rate when the browser insists (Firefox mic). */
  private get sr(): number {
    return this.rate;
  }
}
